import type { ResourceProfileFilterSettings } from "./settings-manager.ts";

/**
 * Decode a stored allow/block filter into the set of enabled resource ids,
 * given the full universe of ids for that kind.
 *  - undefined / empty filter           -> all enabled
 *  - block contains "*"                  -> none enabled
 *  - allow non-empty                     -> exactly the allow entries that exist in allIds
 *  - block of specific ids (no "*")      -> all ids except blocked ones
 * (If both allow and block specific ids are present: start from allow-or-all, then remove blocked.)
 */
export function decodeResourceSelection(
	filter: ResourceProfileFilterSettings | undefined,
	allIds: string[],
): Set<string> {
	// Strict UAC: an unmentioned kind is DENIED — decoding it as "all enabled" would both
	// lie in the editor UI and, on save, convert silent denial into an explicit grant-all.
	if (!filter) {
		return new Set();
	}

	// If block contains "*", nothing is enabled
	if (filter.block?.includes("*")) {
		return new Set();
	}

	// Explicit grant-all: every id enabled except explicitly blocked ones.
	if (filter.allow?.includes("*")) {
		const blocked = new Set(filter.block ?? []);
		return new Set(allIds.filter((id) => !blocked.has(id)));
	}

	// If allow is specified, start with the allow list filtered to valid IDs
	if (filter.allow && filter.allow.length > 0) {
		const allIdsSet = new Set(allIds);
		const enabled = new Set<string>();
		for (const id of filter.allow) {
			if (allIdsSet.has(id)) {
				enabled.add(id);
			}
		}
		// If block is also specified, remove blocked ids
		if (filter.block && filter.block.length > 0) {
			const blockSet = new Set(filter.block);
			for (const id of Array.from(enabled)) {
				if (blockSet.has(id)) {
					enabled.delete(id);
				}
			}
		}
		return enabled;
	}

	// No allow specified, start with all ids
	const enabled = new Set(allIds);
	// Remove any blocked ids
	if (filter.block && filter.block.length > 0) {
		const blockSet = new Set(filter.block);
		for (const id of allIds) {
			if (blockSet.has(id)) {
				enabled.delete(id);
			}
		}
	}
	return enabled;
}

export interface ResourceSelectionIdentity {
	id: string;
}

export interface RemappedResourceSelectionFilter {
	filter: ResourceProfileFilterSettings | undefined;
	missingIds: Set<string>;
}

/**
 * Resolve persisted profile patterns onto the editor's collision-safe resource ids.
 *
 * Runtime filters intentionally accept basenames, parent directory names, globs, and paths. The
 * editor, however, must toggle one concrete resource at a time. This remap expands every matching
 * pattern to the concrete ids it currently selects and preserves only genuinely missing patterns.
 * Saving then migrates ambiguous legacy patterns (for example `index.ts`) to exact concrete ids.
 */
export function remapResourceSelectionFilter<T extends ResourceSelectionIdentity>(
	filter: ResourceProfileFilterSettings | undefined,
	items: readonly T[],
	matchesPattern: (item: T, pattern: string) => boolean,
): RemappedResourceSelectionFilter {
	if (!filter) return { filter: undefined, missingIds: new Set() };

	const missingIds = new Set<string>();
	const remapPatterns = (patterns: string[] | undefined): string[] | undefined => {
		if (patterns === undefined) return undefined;
		const remapped: string[] = [];
		const seen = new Set<string>();
		for (const pattern of patterns) {
			const ids =
				pattern === "*" ? [pattern] : items.filter((item) => matchesPattern(item, pattern)).map((item) => item.id);
			if (ids.length === 0) {
				missingIds.add(pattern);
				ids.push(pattern);
			}
			for (const id of ids) {
				if (seen.has(id)) continue;
				seen.add(id);
				remapped.push(id);
			}
		}
		return remapped;
	};

	const remapped: ResourceProfileFilterSettings = {};
	const allow = remapPatterns(filter.allow);
	const block = remapPatterns(filter.block);
	if (allow !== undefined) remapped.allow = allow;
	if (block !== undefined) remapped.block = block;
	return { filter: remapped, missingIds };
}

/**
 * Options that let the encoder distinguish a genuine grant-all from an all-enabled snapshot of an
 * enumerated grant (e.g. a collapsed universe where every enumerated id happens to be visible).
 *  - originalFilter: the filter the selection was decoded from. A wildcard is preserved as a
 *    wildcard; an enumerated grant is NEVER widened into one.
 *  - grantAll: the caller explicitly chose "grant all (including future)" — force the wildcard.
 */
export interface EncodeResourceSelectionOptions {
	originalFilter?: ResourceProfileFilterSettings;
	grantAll?: boolean;
}

function shouldEncodeGrantAllWildcard(options?: EncodeResourceSelectionOptions): boolean {
	if (options?.grantAll) return true;
	return options?.originalFilter?.allow?.includes("*") ?? false;
}

function encodeSelection(
	enabled: ReadonlySet<string>,
	allIds: readonly string[],
	framing: ResourceFraming,
	options?: EncodeResourceSelectionOptions,
): ResourceProfileFilterSettings | undefined {
	const allIdsSet = new Set(allIds);
	let validEnabledCount = 0;
	for (const id of enabled) {
		if (allIdsSet.has(id)) validEnabledCount++;
	}

	if (allIds.length > 0 && validEnabledCount === allIds.length) {
		return shouldEncodeGrantAllWildcard(options) ? { allow: ["*"] } : { allow: [...allIds] };
	}
	if (allIds.length === 0) return undefined;
	if (validEnabledCount === 0) return { block: ["*"] };

	return framing === "allow"
		? { allow: allIds.filter((id) => enabled.has(id)) }
		: { block: allIds.filter((id) => !enabled.has(id)) };
}

/**
 * Encode an enabled set back into a filter.
 *  - all enabled            -> enumerated { allow: [...allIds] } (or { allow: ["*"] } only when the
 *                              original was already a wildcard / the caller chose grant-all)
 *  - some but not all       -> { allow: [...enabled, in allIds order] }
 *  - none enabled           -> { block: ["*"] }
 */
export function encodeResourceSelection(
	enabled: Set<string>,
	allIds: string[],
	options?: EncodeResourceSelectionOptions,
): ResourceProfileFilterSettings | undefined {
	return encodeSelection(enabled, allIds, "allow", options);
}

export type ResourceFraming = "allow" | "block";

/**
 * Detect the framing of a given resource profile filter settings.
 */
export function detectResourceFraming(filter: ResourceProfileFilterSettings | undefined): ResourceFraming {
	if (!filter) {
		return "block";
	}
	if (filter.allow && filter.allow.length > 0) {
		return "allow";
	}
	if (filter.block && filter.block.length > 0) {
		if (filter.block.includes("*")) {
			return "allow";
		}
		return "block";
	}
	return "block";
}

/**
 * Encode an enabled set back into a filter, respecting the specified framing.
 */
export function encodeResourceSelectionWithFraming(
	enabled: Set<string>,
	allIds: string[],
	framing: ResourceFraming,
	options?: EncodeResourceSelectionOptions,
): ResourceProfileFilterSettings | undefined {
	return encodeSelection(enabled, allIds, framing, options);
}
